import { ElementType } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { allows, type GrantSpec } from '../../util/access';

export type HubCard = {
  id: string;
  title: string;
  description: string;
  path: string;
  /** What opens this card: a grant coordinate, read by `allows`. */
  grant: GrantSpec;
  color: string;
  icon: ElementType;
};

export type HubSection = {
  id: string;
  title?: string;
  cards: HubCard[];
};

type Props = {
  title?: string;
  subtitle?: string;
  sections: HubSection[];
  hideChrome?: boolean;
};

const WorkspaceHub = ({ title, subtitle, sections, hideChrome = false }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { access } = useAuth();

  const principal = { access };
  const visibleSections = sections
    .map((section) => ({
      ...section,
      cards: section.cards.filter((card) => allows(principal, card.grant)),
    }))
    .filter((section) => section.cards.length > 0);

  if (hideChrome && visibleSections.length === 0) return null;

  return (
    <Box
      sx={{
        p: hideChrome ? 0 : { xs: 2, md: 4 },
        maxWidth: 1400,
        mx: 'auto',
        height: hideChrome ? 'auto' : '100%',
        minHeight: 0,
        overflowY: hideChrome ? 'visible' : 'auto',
      }}
    >
      {!hideChrome && (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
            <div>
              <Typography variant="h4" fontWeight={600} component="h1">
                {title}
              </Typography>
              <Typography color="text.secondary">{subtitle}</Typography>
            </div>
          </Stack>
          <Divider sx={{ mb: 4 }} />
        </>
      )}

      {visibleSections.length === 0 ? (
        <Typography color="text.secondary">{t('common:hubs.empty')}</Typography>
      ) : (
        visibleSections.map((section) => (
          <Box key={section.id} sx={{ mb: 4 }}>
            {section.title ? (
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                {section.title}
              </Typography>
            ) : null}
            <Grid container spacing={3}>
              {section.cards.map((card) => {
                const Icon = card.icon;
                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={card.id}>
                    <Card
                      elevation={2}
                      sx={{
                        height: '100%',
                        transition: 'all 0.25s',
                        '&:hover': {
                          transform: 'translateY(-6px)',
                          boxShadow: 8,
                        },
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: alpha(card.color, 0.2),
                      }}
                    >
                      <CardActionArea
                        onClick={() => navigate(card.path)}
                        sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                      >
                        <CardHeader
                          avatar={
                            <Box
                              sx={{
                                bgcolor: alpha(card.color, 0.12),
                                color: card.color,
                                borderRadius: '50%',
                                width: 56,
                                height: 56,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Icon fontSize="large" />
                            </Box>
                          }
                          title={
                            <Typography variant="h6" component="span" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                              {card.title}
                            </Typography>
                          }
                          subheader={card.description}
                          subheaderTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                          sx={{ pb: 1 }}
                        />
                        <CardContent sx={{ flexGrow: 1, pt: 1 }}>
                          <Chip
                            size="small"
                            icon={<OpenInNew fontSize="small" />}
                            label={t('common:hubs.open')}
                            color="primary"
                            variant="outlined"
                          />
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))
      )}
    </Box>
  );
};

export default WorkspaceHub;
